import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setNotes from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetNotes";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography from "@insite/mobius/Typography";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cenSpcOrderNotesDisableHandler from "@tCentro/Handlers/CenSpcOrderNotesDisableHandler";
import setOrderNotes from "@tCentro/Handlers/CENSPCOrderNotesHandler";
import cenSpcShippingFieldCompleted from "@tCentro/Handlers/CenSpcShippingFieldCompletedHandler";
import cenSpcOrderDetailsFieldCompleted from "@tCentro/Handlers/CenSpcOrderDetailsFieldCompletedHandler";
import cenSpcDeliveryFieldCompleted from "@tCentro/Handlers/CenSpcDeliveryFieldCompletedHandler";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { ChangeEvent, Component } from "react";

import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";

import siteMessage from "@insite/client-framework/SiteMessage";
import usePaymentDetailsFormState from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormState";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import setSpcPONumber from "@tCentro/Handlers/CenSPCPONumberHandler";
import {
    scrollToAddressSection,
    scrollToCheckoutSection,
} from "@tCentro/Custom/Utilities/onePageCheckoutScrollToSection";
import ArrowDownIcon from "@tCentro/Icons/ArrowDownIcon";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import ArrowUpIcon from "@tCentro/Icons/ArrowUpIcon";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    return {
        notes: cart?.notes,
        isOrderNotesDisabled: state.t_components.isOrderNotesDisabled.isOrderNotesDisabledValue,
        isBillingAddressUpdateRequired: state.pages.onePageCheckout.isBillingAddressUpdateRequired,
        isUpdatingCustomer: state.pages.onePageCheckout.isUpdatingCustomer,
        isShippingStateDisabledValue: state.t_components.isShippingStateDisabled.isShippingStateDisabledValue,
        isShippingFieldCompleted: state.t_components.isShippingFieldCompleted.isShippingFieldCompletedValue,
        isOrderDetailsFieldCompleted: state.t_components.isOrderDetailsFieldCompleted.isOrderDetailsFieldCompletedValue,
        cenSpcPONumber: state.t_components.SpcPONumber.SpcPONumber,
        isCreditCard: cart?.paymentMethod?.isCreditCard,
        cart,
        cenSpcError: state.t_components.SpcPONumber.SpcPONumberError,
        SpcshowFormErrors: state.t_components.SpcPONumber.SpcshowFormErrors,
        siteName: state.context.website.name,
    };
};

const mapDispatchToProps = {
    setOrderNotes,
    cenSpcOrderNotesDisableHandler,
    cenSpcShippingFieldCompleted,
    cenSpcOrderDetailsFieldCompleted,
    cenSpcDeliveryFieldCompleted,
    setSpcPONumber,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & TextAreaProps;

export interface OnePageCheckOutShippingOrderNotesEntryStyles {
    textArea?: TextAreaProps;
    noteHeading?: InjectableCss;
    container?: InjectableCss;
    specialInstructionStyles?: InjectableCss;
    poNumberText?: TextFieldPresentationProps;
    saveButtonCustom?: ButtonPresentationProps;
    saveButtonCustomDown?: ButtonPresentationProps;
}

export const OnePageCheckOutShippingOrderNotesEntryStyles: OnePageCheckOutShippingOrderNotesEntryStyles = {
    saveButtonCustomDown: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 24px;
            font-size: 17px;
            gap: 3px;
            > svg {
                > path {
                    fill: #8f0446;
                }
            }
            &.nisco-next {
                > svg {
                    > path {
                        fill: #2b5197;
                    }
                }
            }
        `,
    },
    poNumberText: {
        cssOverrides: {
            inputSelect: css`
                background-color: #f2f2f2;
                border-radius: 24px;

                &:focus {
                    border-radius: 24px;
                    padding-left: 12px;
                }
            `,
            formField: css`
                label {
                    font-family: Poppins;
                    font-weight: 600;
                    font-size: 14px;
                    line-height: 16px;
                    padding-left: 10px;
                    color: ${getProp("theme.typography.p.color")};
                }
            `,
        },
    },
    saveButtonCustom: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 24px;
            font-size: 17px;
            gap: 3px;
            > svg {
                fill: #8f0446;
            }
        `,
    },
    specialInstructionStyles: {
        css: css`
            font-family: Poppins;
            font-weight: 400;
            font-style: italic;
            font-size: 12px;
            line-height: 16px;
            margin-left: 12px;
        `,
    },
    textArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            max-height: 100px;
        `,
        cssOverrides: {
            inputSelect: css`
                border-radius: 24px !important;
                padding: 16px !important;
                font-family: Poppins !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                font-weight: ${TYPOGRAPHY.weight.base} !important;
                line-height: ${TYPOGRAPHY.lineHeight.base} !important;
            `,
            formInputWrapper: css`
                margin-top: 8px !important;
            `,
        },
    },
    noteHeading: {
        css: css`
            display: block;
            width: 100%;
            box-sizing: border-box;
            background-color: #dff2df;
            border-radius: 6px;
            padding: 10px 20px;
            font-size: ${TYPOGRAPHY.size.xxlarge} !important;
            font-weight: ${TYPOGRAPHY.weight.bold} !important;
            line-height: 40px !important;
            color: ${getProp("theme.typography.h1.color")};
            margin-bottom: 0 !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.large} !important;
                        font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
                        padding: 0 20px;
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.large} !important;
                        font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
                        padding: 0 20px;
                    `,
                    css``,
                    null,
                ])}
        `,
    },
    container: {
        css: css`
            border: 1px solid #cfd8df;
            border-radius: 12px;
            background-color: #fff;
            padding: 20px !important;
            margin-bottom: 24px;
            &.disabled-container {
                opacity: 0.5;
                cursor: not-allowed;
                input,
                select,
                textarea,
                label {
                    pointer-events: none !important;
                }
            }
            label {
                margin-bottom: 0px;
            }
            textarea {
                border-radius: 40px !important;
                padding: 16px 24px !important;
                background-color: ${COLORS.background.grayShade} !important;
                font-size: ${TYPOGRAPHY.size.tiny} !important;
                line-height: 16px !important;
                font-weight: ${TYPOGRAPHY.weight.base} !important;
                resize: none !important;
                border: 0px !important;
            }
        `,
    },
};

const styles = OnePageCheckOutShippingOrderNotesEntryStyles;

interface State {
    notes?: string;
}

class OnePageCheckOutShippingOrderNotesEntry extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            notes: this.props.notes,
        };
    }

    componentDidMount() {
        this.props.setOrderNotes({ OrderNotes: "" });
        this.props.setSpcPONumber({ SpcPONumber: "" });
        this.updateOrderNotesState();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.isBillingAddressUpdateRequired !== this.props.isBillingAddressUpdateRequired) {
            this.updateOrderNotesState();
        }
    }

    updateOrderNotesState() {
        if (this.props.isBillingAddressUpdateRequired) {
            this.props.cenSpcOrderNotesDisableHandler({
                isOrderNotesDisabledValue: true,
            });
        } else {
            this.props.cenSpcOrderNotesDisableHandler({
                isOrderNotesDisabledValue: false,
            });
        }
    }

    notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            notes: event.currentTarget.value,
        });

        this.props.setOrderNotes({ OrderNotes: event.currentTarget.value });
    };

    handlePONumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        this.props.setSpcPONumber({ SpcPONumber: event.target.value });
    };

    static getDerivedStateFromProps(props: Props, state: State) {
        if (typeof state.notes === "undefined" && typeof props.notes !== "undefined") {
            return {
                notes: props.notes || "",
            };
        }
        return state;
    }

    render() {
        if (typeof this.state.notes === "undefined") {
            return null;
        }

        const checkoutHeaderBackgroundColor = this.props.siteName === "Nisco" ? "#d9ecff" : "#dff2df";

        return (
            <StyledWrapper
                {...styles.container}
                id="opc-section-order-details"
                data-test-selector="opc-section-order-details"
                className={
                    this.props.isShippingStateDisabledValue || !this.props.isShippingFieldCompleted
                        ? "disabled-container"
                        : ""
                }
                onClick={scrollToAddressSection}
            >
                <StyledWrapper
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-start",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="h2"
                        {...styles.noteHeading}
                        style={{ backgroundColor: checkoutHeaderBackgroundColor }}
                    >
                        {translate("3. Order Details")}
                    </Typography>
                </StyledWrapper>
                <TextArea
                    {...styles.textArea}
                    label={
                        <Typography>
                            <Typography
                                style={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: `${getProp("theme.typography.p.color")}`,
                                    lineHeight: "16px",
                                    marginLeft: "12px",
                                }}
                            >
                                {translate("Special Instructions")}
                            </Typography>
                            <Typography
                                style={{
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    fontStyle: "italic",
                                    color: COLORS.text.lighter,
                                    lineHeight: "16px",
                                }}
                            >
                                {translate("(optional)")}
                            </Typography>
                        </Typography>
                    }
                    placeholder={translate("Enter Special Instructions")}
                    value={this.state.notes}
                    onChange={this.notesChangeHandler}
                />
                <Typography as="p" {...styles.specialInstructionStyles}>
                    {siteMessage("OPC_Special_Instructions")}
                </Typography>

                <StyledWrapper
                    css={css`
                        margin-top: 24px;
                    `}
                >
                    {this.props.isCreditCard && (
                        <TextField
                            {...styles.poNumberText}
                            label={
                                <>
                                    <span aria-hidden>{translate("PO Number")}</span>
                                    <Typography
                                        as="span"
                                        css={css`
                                            color: #636161;
                                            font-style: italic;
                                            font-weight: 400;
                                            font-size: 12px;
                                            line-height: 16px;
                                        `}
                                    >
                                        {this.props.cart?.requiresPoNumber ? " " : "(optional)"}
                                    </Typography>
                                    <VisuallyHidden>{translate("Purchase Order Number ")}</VisuallyHidden>
                                </>
                            }
                            maxLength={50}
                            data-test-selector="onePageCheckout_poNumber"
                            value={this.props.cenSpcPONumber}
                            onChange={this.handlePONumberChange}
                            required={this.props.cart?.requiresPoNumber}
                            error={this.props.SpcshowFormErrors && this.props.cenSpcError}
                        />
                    )}
                </StyledWrapper>
                {!this.props.isShippingStateDisabledValue &&
                    this.props.isShippingFieldCompleted &&
                    !this.props.isOrderDetailsFieldCompleted && (
                        <StyledWrapper
                            css={css`
                                // border-top: 1px solid #d8dde3;
                                margin-top: 20px;
                                padding-top: 16px;
                                display: flex;
                                justify-content: flex-end;
                                gap: 12px;
                            `}
                        >
                            {/* <Button
                                {...styles.saveButtonCustom}
                                variant="secondary"
                                onClick={event => {
                                    event.stopPropagation();
                                    scrollToCheckoutSection([
                                        "#opc-section-shipping",
                                        "[data-test-selector='shippingAndBillingSection']",
                                    ]);
                                }}
                            >
                                <ArrowUp></ArrowUp>
                                {translate("Previous")}
                            </Button> */}
                            <Button
                                variant="secondary"
                                {...styles.saveButtonCustomDown}
                                className={this.props.siteName === "Nisco" ? "nisco-next" : undefined}
                                onClick={event => {
                                    event.stopPropagation();
                                    this.props.cenSpcOrderDetailsFieldCompleted({
                                        isOrderDetailsFieldCompletedValue: true,
                                    });
                                    this.props.cenSpcDeliveryFieldCompleted({
                                        isDeliveryFieldCompletedValue: false,
                                    });
                                    scrollToCheckoutSection(["#opc-section-delivery-method"]);
                                }}
                            >
                                <ArrowDownIcon />
                                {translate("Next")}
                            </Button>
                        </StyledWrapper>
                    )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckOutShippingOrderNotesEntry),
    definition: {
        group: "Common",
        displayName: "SPC Order Notes Entry",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
